import { CampaignTypes, TitleType, Variants } from '@shared/constants';
import {
  ButtonFlexAlign,
  FormField,
  HowToPlay,
  MetaTags,
} from '@shared/models';

import { Dict, MixedMode } from '../custom-types.models';

export interface ExtraLink {
  hideInIframe?: boolean;
  title: {
    fontFamily: string;
    text: string;
  };
  link: {
    href: string;
    color: string;
    label: string;
    backgroundColor: string;
    fontFamily?: string;
  };
}

export interface BisCamapignConfig {
  afterTitle: boolean;
  questions: Question[];
  showContactForm?: boolean;
  templateAfterForm?: string[];
  extraLinks?: Array<ExtraLink>;
  templateBeforeChanges?: string[];
  templateAfterContactForm?: string[];
  templateBeforeContactForm?: string[];
}

export interface ChallengesBisTabs {
  label: string;
  color: string;
  fontFamily: string;
  selectedColor: string;
  selectedBorder: string;
  campaignType: CampaignTypes;
}

export interface HelpPage {
  header: {
    goBackButton: {
      text: string;
      backgroundColor: string;
      color: string;
      fontFamily: string;
      borderRadius: string;
    };
    backButton?: {
      imageUrl: string;
      alternativeText: string;
      ariaLabel: string;
    };
    backButtonTemplate?: string[];
    howToPlay: HowToPlay;
    logo?: {
      imageUrl: string;
      alternativeText: string;
      ariaLabel: string;
      // Intrinsic size, required by NgOptimizedImage; CSS sets the rendered height.
      width: number;
      height: number;
    };
    bannerUrl?: MixedMode<string>;
    title?: {
      text: string;
      styles: Dict;
    };
    bannerAlternativeText: string;
    background: string | MixedMode<string>;
    borderBottom?: string;
    borderTop?: string;
  };
  contactUs?: ContactUs;
  templateAfterContactForm?: string[];
  templateBeforeContactForm?: string[];
  faqVariant: FaqVariant;
  templateAfterForm?: string[];
  extraLinks?: Array<ExtraLink>;
  template?: MixedMode<string[]>;
  templateBeforeChanges?: string[];
  loyaltychallengeBis?: BisCamapignConfig;
  displayContactUsUnlogged?: boolean;
  displayFooterUnlogged?: boolean;
  meta: MetaTags;
}

export interface ContactLabelDetails {
  text: string;
  color: string;
  fontFamily: string;
}

export interface ContactLinkDetails {
  text: string;
  href: string;
  color: string;
  fontFamily: string;
  backgroundColor?: string;
}

export interface ContactLinkConfig {
  image?: string;
  link: ContactLinkDetails;
  label: ContactLabelDetails;
}

export enum ContactUsType {
  LINK = 'link',
  TEMPLATE = 'template',
  FORM = 'form',
}

export type ContactUs =
  | {
      type: ContactUsType.LINK;
      config: ContactLinkConfig;
    }
  | {
      type: ContactUsType.TEMPLATE;
      config: string[];
    }
  | {
      type: ContactUsType.FORM;
      config: ContactFormConfig;
    };

export interface ContactFormFirstVariantConfig {
  variant: Variants.VARIANT_1;
  title: ContactLabelDetails;
  subtitle: ContactLabelDetails;
  contactFormBlock: {
    fields: FormField[];
    textAreaLabel: string;
    submitButton: {
      label: string;
      iconUrl: string;
    };
    description?: ContactLabelDetails;
  };
}

export type ContactFormTitle =
  | ({ type: TitleType.TEXT } & ContactLabelDetails)
  | {
      type: TitleType.IMAGE;
      picture: {
        imageUrl: string;
        alternativeText: string;
      };
    };

export interface ContactFormSecondVariantConfig {
  variant: Variants.VARIANT_2;
  title: ContactFormTitle;
  subtitle?: ContactLabelDetails;
  contactFormBlock: {
    fields: FormField[];
    submitButton: {
      color: string;
      label: string;
      fontFamily: string;
      backgroundColor: string;
    };
    textAreaAriaLabel: string;
  };
}

export type ContactFormConfig =
  ContactFormFirstVariantConfig | ContactFormSecondVariantConfig;

export interface HelpForm {
  title: string;
  button: Button;
  picture: string;
  template: string;
  subtitle: string;
  fields: FormField[];
  pictureWidth: string;
  formStyles: Record<string, string>;
  titleStyles: Record<string, string>;
  subtitleStyles: Record<string, string>;
}

export interface Button {
  label: string;
  variant: string;
  styles: Record<string, string>;
  buttonAlignment: ButtonFlexAlign;
}

export type FaqVariant = FaqConfigFirstVariant | FaqConfigSecondVariant;

export interface FaqConfigFirstVariant {
  variant: Variants.VARIANT_1;
  title?: {
    displayWhenCampaignBis: boolean;
  } & (
    | {
        type: TitleType.TEXT;
        label: string;
        styles: Dict;
      }
    | {
        type: TitleType.IMAGE;
        picture: { imageUrl: string; alternativeText: string };
      }
  );
  questions: Question[];
  accordion: Accordion;
}

export interface FaqConfigSecondVariant {
  variant: Variants.VARIANT_2;
  title: {
    label: string;
    styles: Dict;
  };
  questions: Question[];
}

export interface Accordion {
  styles: {
    title: Dict;
    description: Dict;
  };
  iconMore?: string;
  iconLess?: string;
  alternativeTextExpandMore: string;
  alternativeTextExpandLess: string;
}

export interface Question {
  title?: string;
  content: string;
}

export interface SendClaimResponse {
  message: string;
}
